import { zodResolver } from '@hookform/resolvers/zod';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { dynamicInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { isPromotionLineItem } from '@vy/services';
import { getCurrencySymbol } from '@vy/services';
import type { LineItem } from '@vy/types';
import { DEFAULT_CURRENCY, INTEGRATION_PROVIDERS, PRICING_MODELS } from '@vy/types';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import React, { useEffect } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { QueryKey } from 'react-query';
import { vyFormRegister } from '../../../../../utils/react-hook-forms-utils';
import { useDynamicInvoice } from '../../dynamic-invoice-context';
import type { RevisionedLineItemDialogSubmitAction } from '../RevisionedLineItemDialogForm';
import {
  RevisionedLineItemDialogForm,
  RevisionedLineItemDialogSubmitActions,
} from '../RevisionedLineItemDialogForm';
import type { DiscountOption } from './revisioned-line-item-dialog-forms-types/discount-options';
import type { FixedRevisionedLineItemForm } from './revisioned-line-item-dialog-forms-types/fixed-revisioned-line-item-form';
import {
  PromotionRevisionedLineItemFormSchema,
  PROMOTION_REVISIONED_LINE_ITEM_TYPES,
} from './revisioned-line-item-dialog-forms-types/promotion-revisioned-line-item-form';
import type {
  PercentageRevisionedLineItemForm,
  PromotionRevisionedLineItemForm,
  PromotionRevisionedLineItemType,
} from './revisioned-line-item-dialog-forms-types/promotion-revisioned-line-item-form';
import { NetSuiteClassesDropdown } from '../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/NetSuiteClassesDropdown';
import { NetSuiteProductsDropdown } from '../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/NetSuiteProductsDropdown';
import { XeroProductsDropdownGeneric } from '../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/XeroProductsDropdownGeneric';
import { useActivatedIntegration } from '../../../../../hooks/use-activated-integration';

type PromotionRevisionedLineItemDialogFormProps = {
  lineItemId?: string;
  open: boolean;
  onClose?: () => void;
  invoiceId: string;
  defaultValues?: Partial<PromotionRevisionedLineItemForm>;
  submitAction?: RevisionedLineItemDialogSubmitAction;
  queryKeyToInvalidate?: QueryKey;
  lineItems: LineItem[];
  promotionLineItems: LineItem[];
};

export const PromotionRevisionedLineItemDialogForm: React.FC<PromotionRevisionedLineItemDialogFormProps> = ({
  lineItemId,
  open,
  onClose,
  invoiceId,
  defaultValues,
  lineItems,
  submitAction,
  queryKeyToInvalidate,
  promotionLineItems,
}) => {
  const isNetSuiteEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite, { suspense: true });
  const isXeroEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.xero, { suspense: true });
  const form = useForm<PromotionRevisionedLineItemForm>({
    resolver: zodResolver(PromotionRevisionedLineItemFormSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset({ ...defaultValues });
    form.setValue('type', defaultValues?.type ?? 'FixedPromotion');
  }, [defaultValues]);

  const { getValues, setValue, watch } = form;
  watch('type');

  const handleSelectChange = (event: SelectChangeEvent) => {
    const value = event.target.value as keyof typeof PROMOTION_REVISIONED_LINE_ITEM_TYPES;
    setValue('type', PROMOTION_REVISIONED_LINE_ITEM_TYPES[value]);
  };

  const { dynamicInvoiceData } = useDynamicInvoice();

  const revisionedLineItemNameRegister = vyFormRegister(form.register, 'modifiedName');
  const revisionedLineItemDescriptionRegister = vyFormRegister(form.register, 'modifiedDescription');
  const promotionTypeRegister = form.register('type');
  const promotionType = getValues('type');

  const currency = dynamicInvoiceData.customer.settings?.currency ?? DEFAULT_CURRENCY;
  const lineItemType =
    promotionType === PROMOTION_REVISIONED_LINE_ITEM_TYPES.Percentage ? 'Percentage' : 'Fixed';

  const startAdornment =
    lineItemType === 'Fixed' ? (
      <InputAdornment position="start">{getCurrencySymbol(currency)}</InputAdornment>
    ) : null;

  const revisionedLineItemPriceRegister =
    promotionType === PROMOTION_REVISIONED_LINE_ITEM_TYPES.Percentage
      ? form.register('percentage', { valueAsNumber: true })
      : form.register('revenueBreakdown.total', { valueAsNumber: true });
  const title =
    submitAction === RevisionedLineItemDialogSubmitActions.Create
      ? 'Add discount to this invoice'
      : 'Update discount in this invoice';
  const submitButtonLabel =
    submitAction === RevisionedLineItemDialogSubmitActions.Create ? 'Add Item' : 'Update';

  return (
    <RevisionedLineItemDialogForm
      invoiceId={invoiceId}
      lineItemId={lineItemId}
      open={open}
      onClose={onClose}
      lineItemType={promotionType}
      form={form}
      submitAction={submitAction}
      title={title}
      submitButtonLabel={submitButtonLabel}
      queryKeyToInvalidate={queryKeyToInvalidate}
    >
      <TextField
        {...getSelectorAttribute(dynamicInvoicePageSelectors.revision.promotionRevisionModal.itemNameField)}
        {...revisionedLineItemNameRegister}
        label="Discount Name"
        placeholder="Type Discount name Here..."
        size="medium"
        fullWidth
        sx={{ padding: '10px' }}
        error={isNotNullOrUndefined(form.formState.errors[revisionedLineItemNameRegister.name])}
        helperText={form.formState.errors[revisionedLineItemNameRegister.name]?.message}
      />
      <TextField
        {...getSelectorAttribute(
          dynamicInvoicePageSelectors.revision.promotionRevisionModal.descriptionField,
        )}
        {...revisionedLineItemDescriptionRegister}
        label="Description"
        placeholder="Description"
        size="medium"
        fullWidth
        sx={{ padding: '10px' }}
        error={isNotNullOrUndefined(form.formState.errors[revisionedLineItemDescriptionRegister.name])}
        helperText={form.formState.errors[revisionedLineItemDescriptionRegister.name]?.message}
      />
      <Box
        sx={{
          display: 'flex',
          gap: '32px',
          padding: '10px',
        }}
      >
        <FormControl {...promotionTypeRegister} size="medium" variant="standard" fullWidth>
          <InputLabel id="amount-select-label">Amount</InputLabel>
          <Select
            {...getSelectorAttribute(
              dynamicInvoicePageSelectors.revision.promotionRevisionModal.promotionTypeDropdownButton,
            )}
            labelId="amount-select-label"
            id="amount-select"
            displayEmpty
            value={lineItemType}
            onChange={handleSelectChange}
            size="medium"
            input={<DesignInput />}
            sx={{
              '& .MuiSelect-select': {
                backgroundColor: '#F6F6F6',
                border: 'none',
                borderRadius: '12px',
              },
            }}
          >
            {getDiscountOptions(lineItemId, lineItems, promotionLineItems).map((value) => (
              <MenuItem
                key={value.label.toString()}
                value={value.value}
                disabled={value.disabled}
                {...getSelectorAttribute(
                  dynamicInvoicePageSelectors.revision.promotionRevisionModal.promotionTypeList,
                )}
              >
                {value.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          {...getSelectorAttribute(dynamicInvoicePageSelectors.revision.promotionRevisionModal.priceField)}
          {...revisionedLineItemPriceRegister}
          defaultValue={0}
          type="number"
          sx={{
            '& .MuiFilledInput-root .MuiInputBase-input': {
              padding: '10px',
            },
          }}
          size="medium"
          error={!!getAmountError(promotionType, form.formState.errors)}
          slotProps={{
            input: {
              startAdornment,
            }
          }}
        />
      </Box>
      {(isNetSuiteEnabled || isXeroEnabled) && (
        <Stack
          sx={{
            gap: 2,
            padding: 1
          }}>
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 13
            }}>
            ERP Settings
          </Typography>

          {isNetSuiteEnabled && (
            <>
              <NetSuiteProductsDropdown
                fieldPath="netSuiteProduct"
                control={form.control}
                showDropdown
                onChange={(modifiedIntegrationEntity) => {
                  if (isNullOrUndefined(modifiedIntegrationEntity)) {
                    form.setValue('classMetadata', null);
                  }
                }}
              />
              <NetSuiteClassesDropdown control={form.control} fieldPath="classMetadata" />
            </>
          )}

          {isXeroEnabled && (
            <XeroProductsDropdownGeneric fieldPath="xeroProductId" control={form.control} showDropdown />
          )}
        </Stack>
      )}
      <FormHelperText>{getAmountError(promotionType, form.formState.errors) || ' '}</FormHelperText>
    </RevisionedLineItemDialogForm>
  );
};

function getDiscountOptions(
  currentRevisionedLineItemId: string | undefined,
  lineItems: LineItem[],
  promotionLineItems: LineItem[],
): DiscountOption[] {
  const isDisabled = isPercentageDisabled(currentRevisionedLineItemId, lineItems, promotionLineItems);

  return [
    { label: 'Percentage', value: 'Percentage', disabled: isDisabled },
    { label: 'Fixed', value: 'Fixed', disabled: false },
  ];
}

function isPercentageDisabled(
  currentRevisionedLineItemId: string | undefined,
  lineItems: LineItem[],
  promotionLineItems: LineItem[],
) {
  const percentageRevisionedLineItem = lineItems.find(
    (lineItem) =>
      isPromotionLineItem(lineItem) &&
      isNotNullOrUndefined(lineItem.percentage) &&
      lineItem.id !== currentRevisionedLineItemId,
  );
  const percentagePromotion = promotionLineItems.find(
    (promotionLineItem) =>
      promotionLineItem.lineItemProduct.variants[0].pricing.pricingModel ===
      PRICING_MODELS.PercentageDeduction,
  );

  return isNotNullOrUndefined(percentageRevisionedLineItem) || isNotNullOrUndefined(percentagePromotion);
}

function isFixedPromotionErrors(errors: FieldErrors): errors is FieldErrors<FixedRevisionedLineItemForm> {
  return 'price' in errors;
}

function isPercentagePromotionErrors(
  errors: FieldErrors,
): errors is FieldErrors<PercentageRevisionedLineItemForm> {
  return 'percentage' in errors;
}
function getAmountError(type: PromotionRevisionedLineItemType, errors: FieldErrors) {
  if (type === PROMOTION_REVISIONED_LINE_ITEM_TYPES.Percentage && isPercentagePromotionErrors(errors)) {
    return errors.percentage?.message;
  }
  if (type === PROMOTION_REVISIONED_LINE_ITEM_TYPES.Fixed && isFixedPromotionErrors(errors)) {
    return errors.revenueBreakdown?.message;
  }

  return undefined;
}

const DesignInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    fontSize: 16,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}));
